/**
 * Content sanitization utilities for security
 * Prevents XSS attacks by sanitizing user-generated content
 */

/**
 * Simple HTML/script sanitization function
 * Removes potentially dangerous HTML tags and scripts
 * @param {string} content - Content to sanitize
 * @returns {string} - Sanitized content
 */
export const sanitizeNotificationContent = (content) => {
	if (!content || typeof content !== 'string') {
		return '';
	}

	// Remove HTML tags and script content
	const htmlTagsRegex = /<[^>]*>/g;
	const scriptRegex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
	const onEventRegex = /\s*on\w+\s*=\s*["'][^"']*["']/gi;
	const javascriptRegex = /javascript:/gi;
	const dataRegex = /data:/gi;
	
	// First remove script tags
	let sanitized = content.replace(scriptRegex, '');
	
	// Remove event handlers (onclick, onload, etc.)
	sanitized = sanitized.replace(onEventRegex, '');
	
	// Remove javascript: and data: protocols
	sanitized = sanitized.replace(javascriptRegex, '');
	sanitized = sanitized.replace(dataRegex, '');
	
	// Remove all HTML tags
	sanitized = sanitized.replace(htmlTagsRegex, '');
	
	// Decode HTML entities to prevent double encoding
	sanitized = sanitized
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&quot;/g, '"')
		.replace(/&#x27;/g, "'")
		.replace(/&#x2F;/g, '/')
		.replace(/&amp;/g, '&'); // This should be last
	
	// Trim whitespace
	sanitized = sanitized.trim();
	
	// Limit length to prevent DoS
	if (sanitized.length > 500) {
		sanitized = sanitized.substring(0, 497) + '...';
	}
	
	return sanitized;
};

/**
 * Sanitize notification object
 * @param {object} notification - Notification object to sanitize
 * @returns {object} - Sanitized notification object
 */
export const sanitizeNotification = (notification) => {
	if (!notification || typeof notification !== 'object') {
		return null;
	}

	return {
		...notification,
		title: sanitizeNotificationContent(notification.title),
		description: sanitizeNotificationContent(notification.description),
		// Keep other fields as is, but sanitize text content
		metadata: notification.metadata ? {
			...notification.metadata,
			user_name: sanitizeNotificationContent(notification.metadata.user_name),
			form_name: sanitizeNotificationContent(notification.metadata.form_name)
		} : notification.metadata
	};
};

/**
 * Sanitize array of notifications
 * @param {array} notifications - Array of notifications to sanitize
 * @returns {array} - Array of sanitized notifications
 */
export const sanitizeNotifications = (notifications) => {
	if (!Array.isArray(notifications)) {
		return [];
	}

	return notifications
		.map(sanitizeNotification)
		.filter(notification => notification !== null);
};